import React, {FunctionComponent, useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import OurToastContainer from "../../../components/OurToastContainer/OurToastContainer";
import TextField from "@material-ui/core/TextField";
import {Auth} from "aws-amplify";
import {fetchPostBody} from "../../../utils/fetchUtils";
import OurPaperModal from "../../../components/OurPaperModal/OurPaperModal";
import Body1 from "../../../components/Typography/Body1";
import NegativeButton from "../../../components/CustomBottons/NegativeButton";
import Button from "@material-ui/core/Button";
import {normalizeUsername} from "../../../utils/usernameUtils";
import {setPageTitle} from "../../../utils/titleUtils";
import "./DeleteCommission.scss";

const CommissionDeleteToastContainerId = "CommissionDeleteToastContainerId";
const CommissionDeleteToastSuccessId = "CommissionDeleteToastSuccessId";
const CommissionDeleteToastErrorId = "CommissionDeleteToastErrorId";

type DeleteResult = {
  commissionId: string,
  isDeleted: boolean,
  message?: string
}

function parseCommissionIds(rawInput: string): string[] {
  const ids = rawInput.split(/[\s,]+/)
                      .map(normalizeUsername)
                      .filter((id) => id.length > 0);
  return Array.from(new Set(ids));
}

const DeleteCommission: FunctionComponent = () => {

  const [commissionIdsInput, setCommissionIdsInput] = useState<string>("");
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [deletedCount, setDeletedCount] = useState<number>(0);
  const [results, setResults] = useState<DeleteResult[]>([]);

  const commissionIds = useMemo(() => parseCommissionIds(commissionIdsInput), [commissionIdsInput]);

  const notifyError = (message) => toast(message, {type: "error", pauseOnHover: true, toastId: CommissionDeleteToastSuccessId, containerId: CommissionDeleteToastContainerId});
  const notifySuccess = (message) => toast(message, {type: "success", pauseOnHover: true, toastId: CommissionDeleteToastErrorId, containerId: CommissionDeleteToastContainerId});

  useEffect(() => {
    setPageTitle("Delete Commission");
  }, []);

  async function handleCommissionDelete() {
    setIsSubmitting(true);
    setIsConfirmPopupOpen(false);
    setResults([]);
    setDeletedCount(0);

    try {
      const currUser = await Auth.currentAuthenticatedUser({bypassCache: false});
      const token = currUser.getSignInUserSession()?.getIdToken().getJwtToken();
      const runResults: DeleteResult[] = [];

      // Sequential so one bad ID cannot take the rest of the batch down with it
      for (const commissionId of commissionIds) {
        try {
          await fetchPostBody("admin/action", {
            "action": "commissionDelete",
            "commissionId": commissionId
          }, token);
          runResults.push({commissionId, isDeleted: true});
        } catch (err: any) {
          runResults.push({commissionId, isDeleted: false, message: err.message || "Something went wrong"});
        }

        setResults([...runResults]);
        setDeletedCount(runResults.length);
      }

      const failed = runResults.filter((result) => !result.isDeleted);

      if (failed.length === 0) {
        notifySuccess(`Deleted ${runResults.length} commission${runResults.length === 1 ? "" : "s"}!`);
        setCommissionIdsInput("");
      } else {
        notifyError(`Deleted ${runResults.length - failed.length} of ${runResults.length}, ${failed.length} failed`);
        // Leave the failures in the box so they can be retried without re-pasting the whole list
        setCommissionIdsInput(failed.map((result) => result.commissionId).join("\n"));
      }
    } catch (err: any) {
      notifyError(err.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (<>
    <div className=''>
      <h6 className="semi-bold">Comission Delete</h6>
      <div className="padding-bottom max-small-text-width">
        <TextField label="commission IDs (one per line)"
                   onChange={(event) => setCommissionIdsInput(event.target.value)}
                   value={commissionIdsInput}
                   margin={"dense"}
                   disabled={isSubmitting}
                   multiline={true}
                   rows={5}
                   variant={"outlined"}
                   fullWidth={true}/>
        <Body1 className="caption clr-grey">
          {isSubmitting ?
            `Deleting ${Math.min(deletedCount + 1, commissionIds.length)} of ${commissionIds.length}...`
            :
            `${commissionIds.length} ID${commissionIds.length === 1 ? "" : "s"}`}
        </Body1>
        <div className="padding-bottom"/>

        <Button color="primary"
                disabled={isSubmitting || commissionIds.length === 0}
                onClick={(event) => setIsConfirmPopupOpen(true)}
                variant="contained">
          Delete {commissionIds.length > 1 ? `${commissionIds.length} commissions` : "commission"}
        </Button>

        {results.length > 0 &&
          <div className="commission-delete-results padding-block-self">
            {results.map((result) => (
              <Body1 key={result.commissionId}
                     breakWord={true}
                     className={`caption ${result.isDeleted ? "clr-primary-green" : "clr-red"}`}>
                {result.isDeleted ? "deleted" : "failed"} — {result.commissionId}
                {result.message ? `: ${result.message}` : ""}
              </Body1>
            ))}
          </div>
        }

        <OurPaperModal parentToggle={isConfirmPopupOpen}
                       setParentClose={() => setIsConfirmPopupOpen(false)}
                       leftSideElement={<h6 className="semi-bold">Delete Commission</h6>}
        >
          <div className="center-text max-small-text-width flex-col-gap">
            <Body1 className="padding-bottom">
              {commissionIds.length === 1 ?
                `Delete commission ${commissionIds[0]}?`
                :
                `Delete these ${commissionIds.length} commissions? They are deleted one at a time and this cannot be undone.`}
            </Body1>
            <div className="flex-row-gap">
              <NegativeButton onClick={() => setIsConfirmPopupOpen(false)}>
                no
              </NegativeButton>
              <Button color="primary"
                      autoFocus={true}
                      onClick={handleCommissionDelete}
                      variant="contained">
                Yes
              </Button>
            </div>
          </div>
        </OurPaperModal>
      </div>
    </div>

    <OurToastContainer containerId={CommissionDeleteToastContainerId}/>
  </>);
}

export default DeleteCommission;
